import React, { useCallback, useEffect, useState, useRef, forwardRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import HTMLFlipBook from 'react-pageflip';
import { XIcon } from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
interface FlipbookViewerProps {
  file: File;
  onClose: () => void;
}
interface SpreadHalfProps {
  pageNumber: number;
  side: 'left' | 'right';
  halfWidth: number;
  fullWidth: number;
  pageHeight: number;
}
const SpreadHalf = forwardRef<HTMLDivElement, SpreadHalfProps>(({
  pageNumber,
  side,
  halfWidth,
  fullWidth,
  pageHeight
}, ref) => {
  return <div ref={ref} className="book-page" style={{
    width: halfWidth,
    height: pageHeight,
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: '#faf8f5'
  }} data-id="element-3">
        <div style={{
      width: fullWidth,
      height: pageHeight,
      marginLeft: side === 'right' ? -halfWidth : 0,
      position: 'relative',
      overflow: 'hidden'
    }} data-id="element-4">
          <Page pageNumber={pageNumber} width={fullWidth} renderTextLayer={false} renderAnnotationLayer={false} data-id="element-5" />
        </div>
        <div className={side === 'left' ? 'page-lighting-left' : 'page-lighting-right'} data-id="element-6" />
      </div>;
});
export function FlipbookViewer({
  file,
  onClose
}: FlipbookViewerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pdfAspectRatio, setPdfAspectRatio] = useState<number | null>(null);
  const [isFlipping, setIsFlipping] = useState(false);
  const [dimensions, setDimensions] = useState({
    halfWidth: 400,
    fullWidth: 800,
    pageHeight: 560
  });
  const [isOnCover, setIsOnCover] = useState(true);
  const flipBookRef = useRef<any>(null);
  useEffect(() => {
    const url = URL.createObjectURL(file);
    const loadPdfDimensions = async () => {
      try {
        const pdf = await pdfjs.getDocument(url).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({
          scale: 1
        });
        const ratio = viewport.height / viewport.width;
        setPdfAspectRatio(ratio);
      } catch (err) {
        console.error('Failed to load PDF dimensions:', err);
        setPdfAspectRatio(0.65);
      }
    };
    loadPdfDimensions();
    return () => URL.revokeObjectURL(url);
  }, [file]);
  // More space for the book — no side arrows eating into it
  const calculateDimensions = useCallback(() => {
    if (!pdfAspectRatio) return;
    const availableWidth = window.innerWidth * 0.92;
    const availableHeight = window.innerHeight * 0.8;
    let fullWidth = Math.min(availableWidth, 1500);
    let pageHeight = fullWidth * pdfAspectRatio;
    if (pageHeight > availableHeight) {
      pageHeight = availableHeight;
      fullWidth = pageHeight / pdfAspectRatio;
    }
    const halfWidth = fullWidth / 2;
    setDimensions({
      halfWidth,
      fullWidth,
      pageHeight
    });
  }, [pdfAspectRatio]);
  useEffect(() => {
    calculateDimensions();
    window.addEventListener('resize', calculateDimensions);
    return () => window.removeEventListener('resize', calculateDimensions);
  }, [calculateDimensions]);
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        flipBookRef.current?.pageFlip().flipPrev();
      } else if (e.key === 'ArrowRight') {
        flipBookRef.current?.pageFlip().flipNext();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);
  const onDocumentLoadSuccess = ({
    numPages: n
  }: {
    numPages: number;
  }) => {
    setNumPages(n);
  };
  const handleFlip = (e: any) => {
    setCurrentPage(e.data);
    setIsOnCover(e.data === 0 || e.data >= totalFlipbookPages - 1);
  };
  const handleChangeState = (e: any) => {
    // "flipping" when page is turning, "read" when settled
    setIsFlipping(e.data === 'flipping');
  };
  const buildFlipbookPages = () => {
    if (numPages === 0) return [];
    const pages: React.ReactNode[] = [];
    // Front cover: right half of PDF page 1
    pages.push(<SpreadHalf key="front-cover" pageNumber={1} side="right" halfWidth={dimensions.halfWidth} fullWidth={dimensions.fullWidth} pageHeight={dimensions.pageHeight} data-id="element-7" />);
    // Inner spreads
    for (let i = 2; i <= numPages; i++) {
      pages.push(<SpreadHalf key={`page_${i}_left`} pageNumber={i} side="left" halfWidth={dimensions.halfWidth} fullWidth={dimensions.fullWidth} pageHeight={dimensions.pageHeight} data-id="element-8" />);
      pages.push(<SpreadHalf key={`page_${i}_right`} pageNumber={i} side="right" halfWidth={dimensions.halfWidth} fullWidth={dimensions.fullWidth} pageHeight={dimensions.pageHeight} data-id="element-9" />);
    }
    // Back cover: left half of PDF page 1
    pages.push(<SpreadHalf key="back-cover" pageNumber={1} side="left" halfWidth={dimensions.halfWidth} fullWidth={dimensions.fullWidth} pageHeight={dimensions.pageHeight} data-id="element-10" />);
    return pages;
  };
  const flipbookPages = buildFlipbookPages();
  const totalFlipbookPages = flipbookPages.length;
  const getCurrentSpreadLabel = () => {
    if (currentPage === 0) return 'Cover';
    if (currentPage >= totalFlipbookPages - 1) return 'Back Cover';
    const innerSpread = Math.ceil(currentPage / 2);
    return `${innerSpread} / ${numPages - 1}`;
  };
  if (!pdfAspectRatio) {
    return <div className="flex flex-col items-center justify-center min-h-screen w-full bg-neutral-950" data-id="element-11">
        <div className="flex flex-col items-center gap-3" data-id="element-12">
          <div className="w-8 h-8 border-2 border-neutral-700 border-t-neutral-400 rounded-full animate-spin" data-id="element-13" />
          <p className="text-neutral-500 text-sm tracking-wide" data-id="element-14">
            Preparing your book…
          </p>
        </div>
      </div>;
  }
  return <div className="flex flex-col items-center justify-center min-h-screen w-full bg-neutral-950 relative overflow-hidden select-none" data-id="element-15">
      {/* Close button — minimal, top-right */}
      <button onClick={onClose} className="absolute top-5 right-5 w-8 h-8 rounded-full bg-neutral-800/40 hover:bg-neutral-700/60 flex items-center justify-center transition-colors z-50" aria-label="Close viewer" data-id="element-16">
        <XIcon className="w-3.5 h-3.5 text-neutral-500" data-id="element-17" />
      </button>

      {/* Page counter — bottom center, very subtle */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-50" data-id="element-18">
        <p className="text-[11px] text-neutral-600 tracking-widest uppercase font-light" data-id="element-19">
          {getCurrentSpreadLabel()}
        </p>
      </div>

      {/* Book container */}
      <div className="relative z-10" data-id="element-20">
        {/* Page edges on sides when book is open */}
        {!isOnCover && <>
            <div className="book-page-edges-left" style={{
          height: dimensions.pageHeight
        }} data-id="element-21" />
            <div className="book-page-edges-right" style={{
          height: dimensions.pageHeight
        }} data-id="element-22" />
          </>}

        {/* Flipbook */}
        <div className="relative" style={{
        lineHeight: 0
      }} data-id="element-23">
          <Document file={file} onLoadSuccess={onDocumentLoadSuccess} loading={<div className="flex items-center justify-center" style={{
          width: dimensions.fullWidth,
          height: dimensions.pageHeight,
          background: 'rgba(255,255,255,0.02)'
        }} data-id="element-25">
                <div className="flex flex-col items-center gap-3" data-id="element-26">
                  <div className="w-8 h-8 border-2 border-neutral-700 border-t-neutral-400 rounded-full animate-spin" data-id="element-27" />
                  <p className="text-neutral-500 text-sm" data-id="element-28">Loading pages…</p>
                </div>
              </div>} data-id="element-24">
            {numPages > 0 && <HTMLFlipBook width={dimensions.halfWidth} height={dimensions.pageHeight} size="fixed" minWidth={dimensions.halfWidth} maxWidth={dimensions.halfWidth} minHeight={dimensions.pageHeight} maxHeight={dimensions.pageHeight} showCover={true} mobileScrollSupport={true} onFlip={handleFlip} onChangeState={handleChangeState} className="flipbook" ref={flipBookRef} usePortrait={false} startPage={0} drawShadow={true} flippingTime={600} useMouseEvents={true} swipeDistance={30} clickEventForward={true} maxShadowOpacity={0.5} autoSize={false} data-id="element-29">
                {flipbookPages}
              </HTMLFlipBook>}
          </Document>

          {/* Center spine — fades out during page turn */}
          {!isOnCover && <div className={`book-spine-overlay ${isFlipping ? 'is-flipping' : ''}`} data-id="element-30" />}
        </div>

        {/* Shadow beneath */}
        <div className="book-shadow" data-id="element-31" />
      </div>
    </div>;
}